import React, { useState, useEffect, useCallback } from "react";
import "../styles/grid.css";
import { motion } from "framer-motion";
import { SlArrowLeft, SlArrowRight, SlClose } from "react-icons/sl";

interface ImageGridBoxProps {
  images: string[];
  thumbnailLayout: string;
  thumbnailImageSize: string;
}

const ImageGridBox: React.FC<ImageGridBoxProps> = ({
  images,
  thumbnailImageSize,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [loadedImages, setLoadedImages] = useState<boolean[]>(
    Array(images.length).fill(false)
  );
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  useEffect(() => {
    // Ellenőrizzük, hogy minden kép betöltődött-e
    if (loadedImages.every((loaded) => loaded)) {
      setAllImagesLoaded(true); // Ha minden kép betöltődött, frissítjük az állapotot
    }
  }, [loadedImages]);

  const goToNextImage = useCallback(() => {
    setCurrentIndex((currentIndex + 1) % images.length);
    setIsZoomed(false);
  }, [currentIndex, images.length]);

  const goToPreviousImage = useCallback(() => {
    setCurrentIndex((currentIndex - 1 + images.length) % images.length);
    setIsZoomed(false);
  }, [currentIndex, images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen) {
        if (e.key === "ArrowRight") goToNextImage();
        if (e.key === "ArrowLeft") goToPreviousImage();
        if (e.key === "Escape") closeModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goToNextImage, goToPreviousImage]);

  const handleImageLoad = (index: number) => {
    setLoadedImages((prevLoadedImages) => {
      const updatedLoadedImages = [...prevLoadedImages];
      updatedLoadedImages[index] = true;
      return updatedLoadedImages;
    });
  };

  const openModal = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
    setIsZoomed(false);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setZoomPosition({ x, y });
      setIsZoomed(true);
    }
  };

  const handleModalBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  return (
    <div>
      <div>
        {/* Masonry grid */}
        <div className="masonry relative">
          {images.map((img, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{
                opacity: 1,
                transition: { delay: 0.4, duration: 0.6 },
              }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Loading spinner */}
              {!loadedImages[index] && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
                </div>
              )}
              <motion.img
                initial={{ opacity: 0 }}
                loading="lazy"
                whileInView={{
                  opacity: 1,
                  transition: { delay: 0.4, duration: 0.6 },
                }}
                key={index}
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className={`${thumbnailImageSize} cursor-pointer max-h-[90vh] w-auto object-cover`}
                onClick={() => openModal(index)}
                onLoad={() => handleImageLoad(index)}
                style={{
                  opacity: loadedImages[index] ? 1 : 0.5,
                  transition: "opacity 0.5s ease-in-out",
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* Modal */}
        {isOpen &&
          allImagesLoaded && ( // Csak akkor jelenik meg, ha minden kép betöltődött
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
              onClick={handleModalBackgroundClick}
            >
              <div className="absolute top-4 z-50 left-2 px-4 py-2 bg-black bg-opacity-50 text-white text-xs">
                {currentIndex + 1} / {images.length}
              </div>

              {/* close button */}
              <button
                className="absolute top-4 px-4 py-2 z-50 right-4 text-white text-4xl font-bold hover:text-gray-300"
                onClick={closeModal}
              >
                <SlClose />
              </button>

              {/* left button */}
              <button
                onClick={goToPreviousImage}
                className="absolute left-4 top-1/2 z-50 transform -translate-y-1/2 text-white text-4xl font-bold hover:text-gray-300"
              >
                <SlArrowLeft />
              </button>

              {/* right button */}
              <button
                onClick={goToNextImage}
                className="absolute right-4 top-1/2 z-50 transform -translate-y-1/2 text-white text-4xl font-bold hover:text-gray-300"
              >
                <SlArrowRight />
              </button>

              <div className="relative flex flex-col items-center bg-black rounded-lg">
                <div className="relative w-full max-h-screen flex justify-center items-center overflow-hidden">
                  <img
                    src={images[currentIndex]}
                    loading="lazy"
                    alt={`Slide ${currentIndex + 1}`}
                    onClick={handleImageClick}
                    className={`w-auto max-h-[88vh] max-w-[85vw] object-contain transition-transform duration-300 cursor-zoom-in ${
                      isZoomed ? "cursor-zoom-out" : "cursor-zoom-in"
                    }`}
                    style={
                      isZoomed
                        ? {
                            transform: `scale(1.75)`,
                            transformOrigin: `${zoomPosition.x}px ${zoomPosition.y}px`,
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ImageGridBox;
